import React from 'react';
import { formatDate, formatDateTime } from '../../lib/utils';

/**
 * PrintableSummary Component
 * 
 * Generates a comprehensive medical record summary for printing
 * Includes: Patient info, Prescriptions, Lab tests, Imaging tests, Procedures, Clinical notes
 */
export default function PrintableSummary({ 
  patient, 
  prescriptions = [], 
  labTests = [], 
  imagingTests = [], 
  procedures = [],
  admissions = [],
  showHeader = true 
}) {
  const formatPrescriptionDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="printable-summary hidden print:block">
      {/* Print Header */}
      {showHeader && (
        <div className="print-header">
          <h1>HMC Buddy</h1>
          <p>Hospital Management System - Medical Record</p>
          <p>Generated on: {new Date().toLocaleString('en-IN')}</p>
        </div>
      )}

      {/* Patient Information */}
      {patient && (
        <div className="patient-info-section mb-6">
          <h2 className="text-xl font-bold border-b-2 border-slate-800 pb-2 mb-3">
            Patient Information
          </h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><strong>Name:</strong> {patient.full_name}</div>
            <div><strong>Patient ID:</strong> {patient.patient_id}</div>
            <div><strong>Age:</strong> {patient.date_of_birth ? new Date().getFullYear() - new Date(patient.date_of_birth).getFullYear() : 'N/A'} years</div>
            <div><strong>Gender:</strong> {patient.gender || 'N/A'}</div>
            <div><strong>Phone:</strong> {patient.phone || 'N/A'}</div>
            <div><strong>Blood Group:</strong> {patient.blood_group || 'N/A'}</div>
            <div className="col-span-2"><strong>Address:</strong> {patient.address || 'N/A'}</div>
            {patient.allergies && (
              <div className="col-span-2 bg-red-50 p-2 rounded">
                <strong className="text-red-700">⚠️ Allergies:</strong> {patient.allergies}
              </div>
            )}
            {patient.medical_history && (
              <div className="col-span-2">
                <strong>Medical History:</strong> {patient.medical_history}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Prescriptions */}
      {prescriptions && prescriptions.length > 0 && (
        <div className="prescription-section mb-6 page-break-before">
          <h2 className="text-xl font-bold border-b-2 border-slate-800 pb-2 mb-3">
            Prescriptions ({prescriptions.length})
          </h2>
          {prescriptions.map((prescription, idx) => (
            <div key={prescription.id} className="prescription-item mb-4 border p-3">
              <div className="flex justify-between mb-2">
                <div>
                  <strong>Prescription #{idx + 1}</strong>
                  <span className="ml-3 text-sm text-slate-600">
                    Date: {formatPrescriptionDate(prescription.created_at)}
                  </span>
                </div>
                <div className="text-sm">
                  <strong>Doctor:</strong> {prescription.doctor_name || 'N/A'}
                </div>
              </div>
              
              {prescription.diagnosis && (
                <div className="mb-2">
                  <strong>Diagnosis:</strong> {prescription.diagnosis}
                </div>
              )}
              
              {/* Medicines Table */}
              {prescription.medicines && prescription.medicines.length > 0 && (
                <div className="mb-2">
                  <strong className="block mb-1">Medications:</strong>
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Medicine Name</th>
                        <th>Dosage</th>
                        <th>Frequency</th>
                        <th>Duration</th>
                        <th>Instructions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prescription.medicines.map((med, medIdx) => (
                        <tr key={`${med.medicine_name}-${med.dosage}`}>
                          <td>{medIdx + 1}</td>
                          <td><strong>{med.medicine_name}</strong></td>
                          <td>{med.dosage}</td>
                          <td>{med.frequency}</td>
                          <td>{med.duration}</td>
                          <td>{med.instructions || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {prescription.advice && (
                <div className="mt-2 bg-blue-50 p-2 rounded">
                  <strong>Doctor's Advice:</strong> {prescription.advice}
                </div>
              )}
              
              <div className="text-xs text-slate-500 mt-2">
                Status: {prescription.status || 'Active'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lab Tests */}
      {labTests && labTests.length > 0 && (
        <div className="lab-tests-section mb-6 page-break-before">
          <h2 className="text-xl font-bold border-b-2 border-slate-800 pb-2 mb-3">
            Laboratory Tests ({labTests.length})
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Test Name</th>
                <th>Category</th>
                <th>Ordered Date</th>
                <th>Status</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {labTests.map((test, idx) => (
                <tr key={test.id}>
                  <td>{idx + 1}</td>
                  <td><strong>{test.test_name}</strong></td>
                  <td>{test.test_category || 'N/A'}</td>
                  <td>{formatPrescriptionDate(test.created_at)}</td>
                  <td>
                    <span className={`badge ${
                      test.status === 'completed' ? 'bg-emerald-100' : 
                      test.status === 'pending' ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      {test.status || 'Pending'}
                    </span>
                  </td>
                  <td>
                    {test.result ? (
                      <div className="whitespace-pre-wrap">{test.result}</div>
                    ) : (
                      <em className="text-slate-400">Pending</em>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Detailed Results */}
          {labTests.filter(t => t.result && t.result.length > 50).length > 0 && (
            <div className="mt-4">
              <h3 className="font-bold mb-2">Detailed Test Results:</h3>
              {labTests.filter(t => t.result && t.result.length > 50).map((test, idx) => (
                <div key={test.id} className="mb-3 border p-2">
                  <strong>{test.test_name}:</strong>
                  <div className="mt-1 whitespace-pre-wrap text-sm">{test.result}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Imaging Tests */}
      {imagingTests && imagingTests.length > 0 && (
        <div className="imaging-tests-section mb-6 page-break-before">
          <h2 className="text-xl font-bold border-b-2 border-slate-800 pb-2 mb-3">
            Imaging/Radiology Tests ({imagingTests.length})
          </h2>
          {imagingTests.map((test, idx) => (
            <div key={test.id} className="mb-4 border p-3">
              <div className="flex justify-between mb-2">
                <div>
                  <strong>#{idx + 1} {test.test_type} - {test.body_part}</strong>
                </div>
                <div className="text-sm">
                  {formatPrescriptionDate(test.created_at)}
                </div>
              </div>
              
              {test.clinical_notes && (
                <div className="mb-2">
                  <strong>Clinical Notes:</strong> {test.clinical_notes}
                </div>
              )}
              
              {test.result && (
                <div className="bg-slate-50 p-2 rounded">
                  <strong>Findings/Report:</strong>
                  <div className="mt-1 whitespace-pre-wrap text-sm">
                    {test.result}
                  </div>
                </div>
              )}
              
              <div className="text-xs text-slate-500 mt-2">
                Status: {test.status || 'Pending'} | Urgency: {test.urgency || 'Normal'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Procedures */}
      {procedures && procedures.length > 0 && (
        <div className="procedures-section mb-6 page-break-before">
          <h2 className="text-xl font-bold border-b-2 border-slate-800 pb-2 mb-3">
            Procedures ({procedures.length})
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Procedure Name</th>
                <th>Type</th>
                <th>Scheduled Date</th>
                <th>Doctor</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {procedures.map((proc, idx) => (
                <tr key={proc.id}>
                  <td>{idx + 1}</td>
                  <td><strong>{proc.procedure_name}</strong></td>
                  <td>{proc.procedure_type || 'N/A'}</td>
                  <td>{formatPrescriptionDate(proc.scheduled_date)}</td>
                  <td>{proc.doctor_name || 'N/A'}</td>
                  <td>{proc.status || 'Scheduled'}</td>
                  <td>{proc.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Admissions/Clinical Notes */}
      {admissions && admissions.length > 0 && (
        <div className="admissions-section mb-6 page-break-before">
          <h2 className="text-xl font-bold border-b-2 border-slate-800 pb-2 mb-3">
            Admission Records ({admissions.length})
          </h2>
          {admissions.map((admission, idx) => (
            <div key={admission.id} className="mb-4 border p-3">
              <div className="flex justify-between mb-2">
                <div>
                  <strong>Admission #{idx + 1}</strong>
                </div>
                <div className="text-sm">
                  {formatPrescriptionDate(admission.admission_date)} - {admission.discharge_date ? formatPrescriptionDate(admission.discharge_date) : 'Current'}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <div><strong>Room:</strong> {admission.room_number}</div>
                <div><strong>Bed:</strong> {admission.bed_number}</div>
                <div><strong>Doctor:</strong> {admission.doctor_name || 'N/A'}</div>
                <div><strong>Status:</strong> {admission.status}</div>
              </div>
              
              {admission.admission_reason && (
                <div className="mb-2">
                  <strong>Reason for Admission:</strong> {admission.admission_reason}
                </div>
              )}
              
              {admission.admission_notes && (
                <div className="bg-blue-50 p-2 rounded mb-2">
                  <strong>Clinical Notes:</strong>
                  <div className="mt-1 whitespace-pre-wrap text-sm">
                    {admission.admission_notes}
                  </div>
                </div>
              )}
              
              {admission.discharge_summary && (
                <div className="bg-emerald-50 p-2 rounded">
                  <strong>Discharge Summary:</strong>
                  <div className="mt-1 whitespace-pre-wrap text-sm">
                    {admission.discharge_summary}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Print Footer */}
      <div className="print-footer">
        <p>This is a computer-generated medical record from HMC Buddy Hospital Management System</p>
        <p>Printed on: {new Date().toLocaleString('en-IN')} | Document is valid without signature</p>
      </div>
    </div>
  );
}
